import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useEnvironment } from '../contexts/EnvironmentContext';

/**
 * StaleAppGuard — evicts app-scoped URLs that don't belong to the current
 * tenant.
 *
 * Scenario: two tabs are open and the user logs into a DIFFERENT tenant in
 * one of them. The Stytch session cookie is shared browser-wide, so the
 * other tab keeps its /app/{serviceId} URL from the old tenant while every
 * API call now carries the new tenant's JWT. Tenant isolation correctly
 * returns 404/empty for that service, and the page rendered an empty shell
 * with no way out. (Distinct from the dead-session case, which the
 * ProtectedRoute deep-link revalidation + useFetchData 401 handling already
 * cover — here the session is VALID, just for another tenant.) Also covers
 * deleted apps and hand-mangled deep links.
 *
 * Detection is inventory-based, not error-based: EnvironmentContext already
 * fetches the tenant's full app inventory and re-fetches it on every Stytch
 * session refresh, so "URL's app is missing from a NON-EMPTY inventory" is
 * the signal. While the inventory is empty (still loading, or a zero-app
 * tenant) the guard stays silent — those two are indistinguishable, and
 * redirecting during load would misfire on every cold boot.
 *
 * The redirect is a HARD navigation (same convention as
 * EnvironmentContext.updateEnvironment): every in-memory cache in this tab —
 * selected environment, app inventory, app context — may belong to the old
 * tenant, and a fresh boot into Home is the only state we can trust. The
 * old tenant's ?env= is dropped unless the new tenant has an environment of
 * the same name, so EnvironmentContext picks this tenant's default instead
 * of filtering Home by a nonexistent environment.
 */
const StaleAppGuard = () => {
  const { pathname, search } = useLocation();
  const { environments } = useEnvironment();

  useEffect(() => {
    const match =
      matchPath('/app/:selectedAppId/*', pathname) ||
      matchPath('/app/:selectedAppId', pathname);
    const appId = match?.params?.selectedAppId;
    if (!appId || environments.length === 0) {
      return;
    }

    // The inventory endpoint is clamped server-side at 500 rows (most
    // recently active first). A full list may be truncated, so absence no
    // longer proves the app doesn't exist — fail open (rare-case fallback is
    // the old empty-shell behavior, never eviction from a valid page).
    const total = environments.reduce((n, e) => n + (e.apps?.length || 0), 0);
    if (total >= 500) {
      return;
    }

    const appExists = environments.some(
      (env) => env.apps?.some((app) => app.id === appId)
    );
    if (appExists) {
      return;
    }

    const params = new URLSearchParams(search);
    const next = new URLSearchParams();
    const range = params.get('range');
    const env = params.get('env');
    if (range) next.set('range', range);
    if (env && environments.some((e) => e.name === env)) next.set('env', env);

    console.warn(
      `⚠️ STALE-APP-GUARD: app ${appId} is not in the current tenant's inventory — redirecting to /home`
    );
    try {
      localStorage.setItem(
        'lumoz-last-reload-reason',
        JSON.stringify({ reason: 'stale-app-redirect', appId, at: Date.now() })
      );
    } catch {}
    const query = next.toString();
    window.location.href = `/home${query ? `?${query}` : ''}`;
  }, [pathname, search, environments]);

  return null;
};

export default StaleAppGuard;
